// S2 · Genealogy tab (spec 04 §4 S2, slice 04-C-3 — added ONLY now that
// `operation_serial_lot_captures` exists, per the §4 tab rule; an empty tab
// would read as "this work order had no lineage", which is a different and
// false statement).
//
// It renders e3's `serial_lot_captures[]`: what went INTO the run and what came
// OUT of it, each row deep-linking into module 06's trace view. No money is on
// a capture row (§2.14), so nothing here is redacted and every reader of the
// work order sees the same table.
//
// ═══════════════════════════════════════════════════════════════════════════
// THE UX DECISION THIS FILE EXISTS TO MAKE: EMPTY vs NO-CAPTURES
// ═══════════════════════════════════════════════════════════════════════════
// A planner opens this tab because a customer asked which barrel their drum
// came from. "Nothing here" has two completely different meanings and only one
// of them is reassuring:
//
//   * the work order has steps that DEMAND serial capture
//     (`requires_serial_capture`) and none has been recorded yet — the answer
//     is coming, the run is not finished;
//   * no step on this work order asks for serials at all — there is no lineage
//     to find, and waiting for one is a waste of the planner's afternoon.
//
// The empty state says WHICH, by reading the operations the detail payload
// already carries. That is the whole reason this component takes `operations`.
//
// The direction is a labelled chip with its own colour and its own word, never
// an arrow glyph a reader has to decode: "Consumed — went into this order" and
// "Produced — came out of it". A planner reading a recall trace at 19:00 must
// not have to remember which way the arrow points.

import { useMemo, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { useProductIndex, type SerialLotCapture } from "../hooks/production-queries";
import type { WorkOrderOperation } from "../hooks/production-queries";
import { formatQty, formatTimestamp } from "../lib/format";
import { RefLabel } from "./ref-label";

export function GenealogyTab({
  captures,
  operations,
}: {
  captures: readonly SerialLotCapture[];
  operations: readonly WorkOrderOperation[];
}) {
  const { t, i18n } = useTranslation();
  const products = useProductIndex(
    captures.map((c) => c.product_id).filter((id): id is string => id !== null),
  );
  const opBySeq = useMemo(
    () => new Map(operations.map((op) => [op.id, op] as const)),
    [operations],
  );

  if (captures.length === 0) {
    const demanded = operations.some((op) => op.requires_serial_capture);
    return (
      <div className="wo-panel-genealogy" data-testid="genealogy-empty">
        <p className="production-hint" data-empty-kind={demanded ? "pending" : "not-required"}>
          {demanded
            ? t("production.detail.genealogy.emptyPending")
            : t("production.detail.genealogy.emptyNotRequired")}
        </p>
      </div>
    );
  }

  const consumed = captures.filter((c) => c.direction === "consumed");
  const produced = captures.filter((c) => c.direction === "produced");
  // A product the index has not resolved is NOT its uuid (builder report,
  // 2026-08-12 — same class as the Materials history and the S5 station
  // column): a recall table showing `0191f3c1-…` in the product column reads
  // as corrupt data, and reads identically whether the index is still in
  // flight or the id will never resolve.
  const productName = (id: string): ReactNode => {
    const product = products.byId.get(id);
    return (
      <RefLabel
        value={product === undefined ? undefined : `${product.sku} · ${product.name}`}
        id={id}
        pending={products.isPending}
      />
    );
  };

  return (
    <div className="wo-panel-genealogy" data-testid="genealogy-tab">
      <GenealogySection
        direction="consumed"
        rows={consumed}
        opBySeq={opBySeq}
        productName={productName}
        locale={i18n.language}
      />
      <GenealogySection
        direction="produced"
        rows={produced}
        opBySeq={opBySeq}
        productName={productName}
        locale={i18n.language}
      />
    </div>
  );
}

function GenealogySection({
  direction,
  rows,
  opBySeq,
  productName,
  locale,
}: {
  direction: "consumed" | "produced";
  rows: readonly SerialLotCapture[];
  opBySeq: ReadonlyMap<string, WorkOrderOperation>;
  productName: (id: string) => ReactNode;
  locale: string;
}) {
  const { t } = useTranslation();

  const columns: DataTableColumn<SerialLotCapture>[] = [
    {
      key: "identity",
      header: t("production.detail.genealogy.colIdentity"),
      id: true,
      render: (row) => <IdentityCell row={row} />,
    },
    {
      key: "product_id",
      header: t("production.detail.genealogy.colProduct"),
      render: (row) =>
        row.product_id !== null ? (
          productName(row.product_id)
        ) : (
          // §2.14's bare-serial case, named rather than blank: a laser mark
          // with no registered unit behind it has no product to show, and an
          // empty cell would read as a loading failure.
          <span className="production-hint">{t("production.detail.genealogy.noProduct")}</span>
        ),
    },
    {
      key: "qty",
      header: t("production.detail.genealogy.colQty"),
      numeric: true,
      render: (row) => `${formatQty(row.qty, { locale })} ${row.uom}`,
    },
    {
      key: "wo_operation_id",
      header: t("production.detail.genealogy.colStep"),
      render: (row) => {
        const op = opBySeq.get(row.wo_operation_id);
        return op !== undefined ? `${String(op.seq_no)} — ${op.name}` : "";
      },
    },
    {
      key: "captured_at",
      header: t("production.detail.genealogy.colWhen"),
      age: true,
      render: (row) => formatTimestamp(row.captured_at, locale),
    },
    {
      key: "capture_source",
      header: t("production.detail.genealogy.colSource"),
      render: (row) => t(`production.detail.genealogy.source.${row.capture_source}`),
    },
  ];

  return (
    <section
      className="genealogy-section"
      data-testid={`genealogy-${direction}`}
      aria-label={t(`production.detail.genealogy.${direction}Title`)}
    >
      <h4>
        <span className={`genealogy-chip is-${direction}`} data-direction={direction}>
          {t(`production.detail.genealogy.${direction}Label`)}
        </span>{" "}
        {t(`production.detail.genealogy.${direction}Title`)}
      </h4>
      <p className="production-hint">{t(`production.detail.genealogy.${direction}Body`)}</p>
      {rows.length === 0 ? (
        // Half-empty is a real state and says so: a run whose drums are
        // labelled but whose input was never scanned is a common, honest
        // shape, and rendering nothing at all would hide which half is missing.
        <p className="production-hint" data-testid={`genealogy-${direction}-none`}>
          {t(`production.detail.genealogy.${direction}None`)}
        </p>
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          empty={null}
          rowProps={(row) => ({ "data-testid": `genealogy-row-${row.id}` })}
        />
      )}
    </section>
  );
}

/**
 * The identity a capture names, with the module-06 deep links.
 *
 * A capture can name up to three things at once and each is a different way to
 * find the drum again, so all three render:
 *  * the STOCK UNIT → `/inventory/units/{id}`, the unit card;
 *  * the LOT → `/inventory/lots/{id}/trace`, module 06's trace view (§4.7) —
 *    the screen that answers "which barrel did this come from";
 *  * the engraved SERIAL, as text: it is the number physically on the cable and
 *    the only one an operator on the phone can read back.
 */
function IdentityCell({ row }: { row: SerialLotCapture }) {
  const { t } = useTranslation();
  return (
    <span className="genealogy-identity">
      {row.serial_no !== null ? (
        <span className="genealogy-serial" data-testid="genealogy-serial">
          {row.serial_no}
        </span>
      ) : null}
      {row.stock_unit_id !== null ? (
        <Link
          to="/inventory/units/$id"
          params={{ id: row.stock_unit_id }}
          data-testid="genealogy-unit-link"
        >
          {t("production.detail.genealogy.openUnit")}
        </Link>
      ) : null}
      {row.lot_id !== null ? (
        <Link
          to="/inventory/lots/$id/trace"
          params={{ id: row.lot_id }}
          data-testid="genealogy-trace-link"
        >
          {t("production.detail.genealogy.openTrace")}
        </Link>
      ) : null}
    </span>
  );
}
